import {Draft} from 'immer';

import {assert} from 'Asserts';
import {LookupStepType} from 'lookup-config/LookupConfigModalConstants';
import {getRouterNodeIdsBetweenNodes} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/TraverseFlowchart';
import {FlowchartChanges} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/useFlowchartState/FlowchartStateTypes';

import {
  CustomMappingData,
  TaskSchemaNodeSectionType,
  TransitionSource,
} from '../processFlowchartConfigEditorConstants';
import {isStaticRouterNodeData} from '../processFlowchartConfigEditorNodeTypes';
import {
  findNodeIdsByPTDVClientId,
  FlowchartEdgeChangeType,
  FlowchartNodeChange,
  FlowchartNodeChangeType,
  TransitionChange,
} from '../processFlowchartConfigEditorUtils';
import {FlowchartConfigWithMetadata} from '../redux/flowchartConfigAutosaveManagerCallbacks';
import {
  addDataFlowEdgesForTransition,
  removeDataFlowEdgesForTransition,
} from '../types/transition/transitionDataFlowEdgeSync';
import {parseFieldPairKey} from '../types/transition/transitionKeyUtils';
import {findSubsetTransitionsPromotedToMerge} from '../types/transition/transitionLifecycleUtils';
import {Transition} from '../types/transition/TransitionTypes';
import {getEmptyFlowchartChanges} from './flowchartChangesUtils';
import getNodeChangesForNewEdge from './getNodeChangesForNewEdge';

/**
 * Clears router expressions between two nodes.
 * When the transition source changes, router configurations that depend on it become invalid
 * and need to be cleared. The default function spec is not cleared because it does not have
 * an expression associated with it.
 */
function clearRouterExpressionsBetweenNodes(
  draft: Draft<FlowchartConfigWithMetadata>,
  sourceNodeId: string,
  targetNodeId: string
): Array<FlowchartNodeChange> {
  const nodeChanges: Array<FlowchartNodeChange> = [];
  const routerNodeIds = getRouterNodeIdsBetweenNodes(
    {
      nodes: draft.staticFlowchart.nodesStaticData.map((node) => ({id: node.id, data: node})),
      edges: draft.staticFlowchart.edgesStaticData,
    },
    sourceNodeId,
    targetNodeId
  );

  for (const routerNodeId of routerNodeIds) {
    const routerNode = draft.staticFlowchart.nodesStaticData.find((node) => node.id === routerNodeId)!;
    assert(isStaticRouterNodeData(routerNode), 'Expected router node');
    // Avoiding nested produce statements via spread to prevent issues with unexpectedly modifying a draft
    const newData = {
      ...routerNode,
      routerConfig: {
        ...routerNode.routerConfig,
        functionSpecs: routerNode.routerConfig.functionSpecs.map((spec) => ({
          ...spec,
          expression: null,
          isConfigured: false,
        })),
      },
    };
    nodeChanges.push({
      id: routerNodeId,
      type: FlowchartNodeChangeType.REPLACE,
      data: newData,
    });
  }

  return nodeChanges;
}

function transformCustomMappingForTransitionSourceChange({
  customMapping,
  previousTransitionSource,
  newTransitionSource,
  sourceSectionType,
}: {
  customMapping: CustomMappingData;
  previousTransitionSource: TransitionSource;
  newTransitionSource: TransitionSource;
  sourceSectionType: TaskSchemaNodeSectionType;
}): CustomMappingData {
  if (customMapping.lookupConfig == null) {
    return customMapping;
  }

  // PROCESS_TASK navigates from the output schema to the task schema. It's only needed when:
  //   - The transition source is OUTPUTS (from_schema = output schema), AND
  //   - The source field is from the INPUTS section (field lives on the task schema)
  // For OUTPUTS section fields, the SCHEMA_FIELD already references a field on the output schema,
  // so adding PROCESS_TASK would incorrectly navigate to the task schema.
  // MAP_FROM_START uses FLOWCHART_PROCESS_TASK for navigation, independent of transition source.

  if (sourceSectionType === TaskSchemaNodeSectionType.INPUTS) {
    const lookupSteps = [...customMapping.lookupConfig.lookupSteps];

    if (
      previousTransitionSource === TransitionSource.INPUTS &&
      newTransitionSource === TransitionSource.OUTPUTS
    ) {
      if (lookupSteps.length === 0 || lookupSteps[0].type !== LookupStepType.PROCESS_TASK) {
        lookupSteps.unshift({type: LookupStepType.PROCESS_TASK});
      }
    } else if (
      previousTransitionSource === TransitionSource.OUTPUTS &&
      newTransitionSource === TransitionSource.INPUTS
    ) {
      if (lookupSteps.length > 0 && lookupSteps[0].type === LookupStepType.PROCESS_TASK) {
        lookupSteps.shift();
      }
    }

    return {
      ...customMapping,
      lookupConfig: {
        ...customMapping.lookupConfig,
        lookupSteps,
      },
    };
  }

  if (sourceSectionType === TaskSchemaNodeSectionType.OUTPUTS) {
    // OUTPUTS fields should never have a leading PROCESS_TASK step. Strip it defensively
    // in case legacy configs were created with one under old logic.
    const lookupSteps = [...customMapping.lookupConfig.lookupSteps];
    if (lookupSteps.length > 0 && lookupSteps[0].type === LookupStepType.PROCESS_TASK) {
      lookupSteps.shift();
    }
    if (lookupSteps.length !== customMapping.lookupConfig.lookupSteps.length) {
      return {
        ...customMapping,
        lookupConfig: {
          ...customMapping.lookupConfig,
          lookupSteps,
        },
      };
    }
    return customMapping;
  }

  // MAP_FROM_START: navigation is handled by FLOWCHART_PROCESS_TASK, unaffected by transition source.
  return customMapping;
}

/**
 * Applies autohealing side effects when a transition source changes.
 * This clears router expressions between affected nodes.
 */
function healTransitionSourceChange(
  draft: Draft<FlowchartConfigWithMetadata>,
  sourcePTDVId: string,
  targetPTDVId: string
): {nodeChanges: Array<FlowchartNodeChange>} {
  const nodeChanges: Array<FlowchartNodeChange> = [];

  const sourceNodeIds = findNodeIdsByPTDVClientId(draft.staticFlowchart.nodesStaticData, sourcePTDVId);
  const targetNodeIds = findNodeIdsByPTDVClientId(draft.staticFlowchart.nodesStaticData, targetPTDVId);

  for (const sourceNodeId of sourceNodeIds) {
    for (const targetNodeId of targetNodeIds) {
      nodeChanges.push(...clearRouterExpressionsBetweenNodes(draft, sourceNodeId, targetNodeId));
    }
  }

  return {nodeChanges};
}

function applyTransitionSourceUpdate(
  draft: Draft<FlowchartConfigWithMetadata>,
  existing: Transition,
  transitionSourceChange: {sourcePTDVId: string; value: TransitionSource},
  sideEffects: FlowchartChanges
): void {
  const previousValue = existing.transitionSourcesBySourcePTDVId[transitionSourceChange.sourcePTDVId];
  existing.transitionSourcesBySourcePTDVId[transitionSourceChange.sourcePTDVId] =
    transitionSourceChange.value;

  if (transitionSourceChange.value === previousValue) {
    return;
  }

  const healingResult = healTransitionSourceChange(
    draft,
    transitionSourceChange.sourcePTDVId,
    existing.targetPTDVId
  );
  sideEffects.nodeChanges.push(...healingResult.nodeChanges);

  if (previousValue != null) {
    const transformedMappings: Record<string, CustomMappingData | null> = {};
    for (const [fieldPairKey, customMapping] of Object.entries(existing.customMappingDataByFieldPair)) {
      const parsedKey = parseFieldPairKey(fieldPairKey);
      transformedMappings[fieldPairKey] =
        customMapping != null && parsedKey != null
          ? transformCustomMappingForTransitionSourceChange({
              customMapping,
              previousTransitionSource: previousValue,
              newTransitionSource: transitionSourceChange.value,
              sourceSectionType: parsedKey.sourceSectionType,
            })
          : customMapping;
    }
    existing.customMappingDataByFieldPair = transformedMappings;
  }
}

function applyIsPlannedUpdate(
  draft: Draft<FlowchartConfigWithMetadata>,
  existing: Transition,
  isPlanned: boolean,
  sideEffects: FlowchartChanges
): void {
  const wasPlanned = existing.isPlanned;
  const isPromoting = !wasPlanned && isPlanned;
  existing.isPlanned = isPlanned;

  if (wasPlanned) {
    const removedEdgeIds = removeDataFlowEdgesForTransition(draft.staticFlowchart, existing);
    for (const edgeId of removedEdgeIds) {
      sideEffects.edgeChanges.push({type: FlowchartEdgeChangeType.REMOVE, id: edgeId});
    }
    return;
  }

  const addedEdges = addDataFlowEdgesForTransition(draft.staticFlowchart, existing);
  for (const edge of addedEdges) {
    sideEffects.nodeChanges.push(
      ...getNodeChangesForNewEdge(draft.staticFlowchart, edge, draft.metadata.rootNodeStaticData)
    );
  }

  if (isPromoting && existing.sourcePTDVIds.length > 1) {
    // Delete redundant subset transitions directly rather than emitting 'remove'
    // transition changes. The remove path runs removeDataFlowEdgesForTransition,
    // which would delete data-flow edges that may still be needed by the newly-promoted
    // merging transition (since edge removal is keyed on field-pair, not transition).
    deletePromotedSubsetTransitions(draft, existing);
  }
}

function deletePromotedSubsetTransitions(
  draft: Draft<FlowchartConfigWithMetadata>,
  mergingTransition: Transition
): void {
  for (const id of findSubsetTransitionsPromotedToMerge({
    mergingTransition,
    transitions: draft.metadata.transitions,
    nodes: draft.staticFlowchart.nodesStaticData,
    edges: draft.staticFlowchart.edgesStaticData,
  })) {
    delete draft.metadata.transitions[id];
  }
}

export function applyTransitionChanges(
  transitionChanges: Array<TransitionChange>,
  draft: Draft<FlowchartConfigWithMetadata>
): FlowchartChanges {
  const sideEffects = getEmptyFlowchartChanges();
  transitionChanges.forEach((change) => {
    switch (change.type) {
      case 'add':
        draft.metadata.transitions[change.transition.id] = change.transition;
        if (change.transition.isPlanned) {
          const addedEdges = addDataFlowEdgesForTransition(draft.staticFlowchart, change.transition);
          for (const edge of addedEdges) {
            sideEffects.nodeChanges.push(
              ...getNodeChangesForNewEdge(draft.staticFlowchart, edge, draft.metadata.rootNodeStaticData)
            );
          }
          if (change.transition.sourcePTDVIds.length > 1) {
            deletePromotedSubsetTransitions(draft, change.transition);
          }
        }
        break;
      case 'remove': {
        const existing = draft.metadata.transitions[change.id];
        if (existing != null && existing.isPlanned) {
          const removedEdgeIds = removeDataFlowEdgesForTransition(draft.staticFlowchart, existing);
          for (const edgeId of removedEdgeIds) {
            sideEffects.edgeChanges.push({type: FlowchartEdgeChangeType.REMOVE, id: edgeId});
          }
        }
        delete draft.metadata.transitions[change.id];
        break;
      }
      case 'update': {
        const existing = draft.metadata.transitions[change.id];
        if (existing == null) {
          break;
        }
        if (change.changes.transitionSource != null) {
          applyTransitionSourceUpdate(draft, existing, change.changes.transitionSource, sideEffects);
        }
        if (change.changes.customMapping != null) {
          const {fieldPairKey, value: customMappingValue} = change.changes.customMapping;
          existing.customMappingDataByFieldPair[fieldPairKey] = customMappingValue;
        }
        if (change.changes.isPlanned != null && change.changes.isPlanned !== existing.isPlanned) {
          applyIsPlannedUpdate(draft, existing, change.changes.isPlanned, sideEffects);
        }
        break;
      }
      default:
        assert(false, 'Unexpected transition change type', {change});
    }
  });

  return sideEffects;
}
